import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { useTheme } from '@mui/material/styles';

import type { TrendPoint } from '@/types/generated';
import { formatMoneyCompact, formatMonth } from '@/features/reporting/formatting';

/**
 * The four charts that share one monthly x-axis.
 *
 * NULLS ARE PASSED THROUGH, NOT COERCED. Every series here can legitimately have a gap — a month
 * with nothing decided has no win rate, a month with no recorded costs has no margin — and
 * `@mui/x-charts` draws a break in the line for null. Substituting zero would draw a plunge to the
 * floor and back, which reads as a catastrophic month rather than a quiet one. `connectNulls` is
 * deliberately off for the same reason.
 *
 * Every chart is bounded by the same `points` array, so the axes cannot drift apart between
 * panels the way they would if each fetched its own window.
 */

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack spacing={0.25} sx={{ mb: 1 }}>
          <Typography variant="h3">{title}</Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Stack>
        {/* A fixed height, because a chart in a flex row with no intrinsic size collapses to
            nothing on first paint and then jumps. */}
        <Box sx={{ height: 260 }}>{children}</Box>
      </CardContent>
    </Card>
  );
}

export function TrendCharts({ points, currency }: { points: TrendPoint[]; currency: string }) {
  const theme = useTheme();
  const months = points.map((p) => formatMonth(p.month));

  const axis = { scaleType: 'band' as const, data: months };

  return (
    <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' } }}>
      <ChartCard title="Revenue won" subtitle={`Accepted quotes, ${currency}`}>
        <BarChart
          xAxis={[axis]}
          yAxis={[{ valueFormatter: (v: number) => formatMoneyCompact(v, currency) }]}
          series={[
            {
              data: points.map((p) => p.won_value_minor),
              label: 'Won',
              color: theme.palette.primary.main,
              valueFormatter: (v) => (v === null ? '—' : formatMoneyCompact(v, currency)),
            },
          ]}
          margin={{ left: 70 }}
          slotProps={{ legend: { hidden: true } }}
        />
      </ChartCard>

      <ChartCard title="Win rate" subtitle="Won ÷ decided. Expiry is not a decision.">
        <LineChart
          xAxis={[axis]}
          yAxis={[{ min: 0, max: 100, valueFormatter: (v: number) => `${v}%` }]}
          series={[
            {
              // Divided here rather than server-side: the wire carries basis points so nothing
              // round-trips through a float.
              data: points.map((p) =>
                p.win_rate_basis_points === null ? null : p.win_rate_basis_points / 100,
              ),
              label: 'Win rate',
              color: theme.palette.success.main,
              connectNulls: false,
              valueFormatter: (v) => (v === null ? 'nothing decided' : `${v.toFixed(1)}%`),
            },
          ]}
          margin={{ left: 55 }}
          slotProps={{ legend: { hidden: true } }}
        />
      </ChartCard>

      <ChartCard title="Margin on won business" subtitle="Mean, weighted by quotes">
        <LineChart
          xAxis={[axis]}
          yAxis={[{ valueFormatter: (v: number) => `${v}%` }]}
          series={[
            {
              data: points.map((p) =>
                p.avg_margin_basis_points === null ? null : p.avg_margin_basis_points / 100,
              ),
              label: 'Margin',
              color: theme.palette.info.main,
              connectNulls: false,
              area: true,
              valueFormatter: (v) => (v === null ? 'no cost recorded' : `${v.toFixed(1)}%`),
            },
          ]}
          margin={{ left: 55 }}
          slotProps={{ legend: { hidden: true } }}
        />
      </ChartCard>

      <ChartCard
        title="Prediction accuracy"
        subtitle="Mean absolute error against the price a human actually charged. Lower is better."
      >
        <LineChart
          xAxis={[axis]}
          yAxis={[{ valueFormatter: (v: number) => `${v}%` }]}
          series={[
            {
              data: points.map((p) =>
                p.ai_error_basis_points === null ? null : p.ai_error_basis_points / 100,
              ),
              label: 'Mean absolute error',
              color: theme.palette.warning.main,
              connectNulls: false,
              valueFormatter: (v, ctx) => {
                if (v === null) return 'no recommendation logged';
                const sample = points[ctx.dataIndex]?.ai_sample ?? 0;
                // The sample travels into the tooltip: an error over three quotes and over three
                // hundred draw at the same height and mean very different things.
                return `${v.toFixed(1)}% over ${sample} quote${sample === 1 ? '' : 's'}`;
              },
            },
          ]}
          margin={{ left: 55 }}
          slotProps={{ legend: { hidden: true } }}
        />
      </ChartCard>
    </Box>
  );
}
